import { Tirage } from "@/api/tirages/tirage.api";
import i18n, { SupportedLocale } from "@/i18n";
import { computeFieldTirageDimensions } from "@/lib/computeFields";
import { moneyAmountToMajorUnits, MoneyAmount } from "@/lib/shared-types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { AdvancedSearchConfig, attachmentPhotoAdvancedSearchGroup } from "../forms/advanced-search/advanced-search-fields-def";
import {
  artistLastNameSortGroup,
  artworkRelatedAuthorLastNameSortField,
} from "../forms/advanced-search/artist-sort-fields";
import { ColumnFormatted } from "./general";

export const tiragesColumns: ColumnFormatted[] = [
  {
    type: "string",
    key: "tirageDescriptionNumeroEnregistrement",
    visible: true,
  },
  { type: "image", key: "vignette", visible: true },
  { type: "string", key: "tirageDescriptionNumeroInventaire", visible: true },
  { type: "string", key: "tirageDescriptionNumeroAutre", visible: false },
  {
    type: "boolean",
    key: "tirageDescriptionNumeroAutrePublished",
    visible: false,
  },
  { type: "string", key: "tirageDescriptionNumeroAncien", visible: false },
  {
    type: "boolean",
    key: "tirageDescriptionNumeroAncienPublished",
    visible: false,
  },
  {
    type: "computed",
    key: "tirageDescriptionType",
    visible: true,
    formattedData(data) {
      if (Object.keys(data.tirageDescriptionType ?? {}).length === 0) return "";
      return (
        data.tirageDescriptionType?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },

  {
    type: "computed",
    key: "tirageDescriptionDimensionsWithMargins",
    formattedData: (data: Tirage) => {
      return computeFieldTirageDimensions(
        {
          unknownDimensions: false,
          ...(parseJsonOrUndefined(
            data.tirageDescriptionDimensionsWithMargins,
          ) ?? {}),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
    visible: true,
  },
  {
    type: "computed",
    key: "tirageDescriptionDimensionsWithoutMargins",
    formattedData: (data: Tirage) => {
      return computeFieldTirageDimensions(
        {
          unknownDimensions: false,
          ...(parseJsonOrUndefined(
            data.tirageDescriptionDimensionsWithoutMargins,
          ) ?? {}),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
    visible: true,
  },
  { type: "string", key: "tirageDescriptionDateCreation", visible: true },
  {
    type: "computed",
    key: "tirageDescriptionPrinter",
    visible: true,
    formattedData(data) {
      if (Object.keys(data.tirageDescriptionPrinter ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionPrinter?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },
  { type: "richtext", key: "tirageDescriptionTechnicalNotes" },
  { type: "richtext", key: "tirageDescriptionConditionNotes" },
  {
    type: "multilingual",
    key: "tirageRemarquesPubliquesMultilingual",
  },
  { type: "boolean", key: "tirageRemarquesPubliquesPublished" },
  { type: "richtext", key: "tirageDescriptionNotes" },
  {
    type: "computed",
    key: "tirageDescriptionTechnique",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionTechnique ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionTechnique?.[i18n.language as SupportedLocale] ??
        ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSupport",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionSupport ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionSupport?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionEditionNumber",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionEditionNumber ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionEditionNumber?.[
          i18n.language as SupportedLocale
          ] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSignatureInscription",
    formattedData: (data: Tirage) => {
      if (
        Object.keys(data.tirageDescriptionSignatureInscription ?? {}).length ===
        0
      )
        return "";
      return (
        data.tirageDescriptionSignatureInscription?.[
          i18n.language as SupportedLocale
          ] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSignatureInscriptionDos",
    formattedData: (data: Tirage) => {
      if (
        Object.keys(data.tirageDescriptionSignatureInscriptionDos ?? {})
          .length === 0
      )
        return "";
      return (
        data.tirageDescriptionSignatureInscriptionDos?.[
          i18n.language as SupportedLocale
          ] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSignatureInscriptionDos",
    formattedData: (data: Tirage) => {
      if (
        Object.keys(data.tirageDescriptionSignatureInscriptionDos ?? {})
          .length === 0
      )
        return "";
      return (
        data.tirageDescriptionSignatureInscriptionDos?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  { type: "string", key: "tirageEstimationDescription" },
  {
    type: "computed",
    key: "tirageEstimationAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageEstimationAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "status", key: "tiragePublicationStatus" },
  { type: "string", key: "tirageStatus", visible: false },
  { type: "string", key: "tirageSaleDescription" },
  {
    type: "computed",
    key: "tirageSaleAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageSaleAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageAcquisitionDescription" },
  {
    type: "computed",
    key: "tirageAcquisitionAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageAcquisitionAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageProductionDescription" },
  {
    type: "computed",
    key: "tirageProductionAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageProductionAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageEncadrementDescription" },
  {
    type: "computed",
    key: "tirageEncadrementAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageEncadrementAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageAssuranceDescription" },
  {
    type: "computed",
    key: "tirageAssuranceAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageAssuranceAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      const amount = moneyAmountToMajorUnits(parsedData.amount);
      if (amount == null) return "";
      return `${amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageStorageLocation1" },
  { type: "string", key: "tirageStorageLocation2" },
  { type: "string", key: "tirageStorageLocation3" },
];

export const tiragesColumnsVisibility = tiragesColumns.reduce(
  (acc, column) => ({
    ...acc,
    [column.key]: column.visible || false,
  }),
  {},
);

export const tiragesAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "tiragesDescription",
    fields: [
      { type: "string", key: "tirageDescriptionNumeroEnregistrement" },
      { type: "string", key: "tirageDescriptionNumeroInventaire" },
      { type: "string", key: "tirageDescriptionNumeroAutre" },
      { type: "boolean", key: "tirageDescriptionNumeroAutrePublished" },
      { type: "string", key: "tirageDescriptionNumeroAncien" },
      { type: "boolean", key: "tirageDescriptionNumeroAncienPublished" },
      { type: "multilingual", key: "tirageDescriptionType" },
      { type: "string", key: "tirageDescriptionDateCreation" },
      { type: "multilingual", key: "tirageDescriptionPrinter" },
      { type: "string", key: "tirageDescriptionTechnicalNotes" },
      { type: "string", key: "tirageDescriptionConditionNotes" },
      {
        type: "multilingual",
        key: "tirageRemarquesPubliquesMultilingual",
      },
      { type: "boolean", key: "tirageRemarquesPubliquesPublished" },
      { type: "string", key: "tirageDescriptionNotes" },
      { type: "multilingual", key: "tirageDescriptionTechnique" },
      { type: "multilingual", key: "tirageDescriptionSupport" },
      { type: "multilingual", key: "tirageDescriptionEditionNumber" },
      { type: "multilingual", key: "tirageDescriptionSignatureInscription" },
      { type: "string", key: "tirageEstimationDescription" },
      { type: "currency", key: "tirageEstimationAmount" },
      { type: "string", key: "tirageSaleDescription" },
      { type: "currency", key: "tirageSaleAmount" },
      { type: "string", key: "tirageAcquisitionDescription" },
      { type: "currency", key: "tirageAcquisitionAmount" },
      { type: "string", key: "tirageProductionDescription" },
      { type: "currency", key: "tirageProductionAmount" },
      { type: "string", key: "tirageEncadrementDescription" },
      { type: "currency", key: "tirageEncadrementAmount" },
      { type: "string", key: "tirageAssuranceDescription" },
      { type: "currency", key: "tirageAssuranceAmount" },
      { type: "string", key: "tirageStorageLocation1" },
      { type: "string", key: "tirageStorageLocation2" },
      { type: "string", key: "tirageStorageLocation3" },
      { type: "string", key: "tirageStatus" },
    ],
  },
  {
    type: "group",
    label: "bibliographyLabelExport",
    fields: [
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionReproduitPage",
      },
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionReproduitPlanche",
      },
      {
        type: "boolean",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionPlancheNonNumerotee",
      },
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionCitePage",
      },
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionListePage",
      },
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionSousNumero",
      },
      {
        type: "boolean",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionReproductionCouleur",
      },
      {
        type: "boolean",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionReproductionCouverture",
      },
      {
        type: "boolean",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionNonPagine",
      },
      {
        type: "string",
        key: "tirageInBibliography.some.tirageDansBibliographieDescriptionTexteLibre",
      },
    ],
  },
  artistLastNameSortGroup(artworkRelatedAuthorLastNameSortField),
  attachmentPhotoAdvancedSearchGroup,
];
